/**
  * 批量计算组件
  * 
  * 版本: 1.1.0
  * 变更记录:
  * - 2025-09-29: 新增“下载批量模板”按钮，从 public/【模板】批量计算回本-0910.xlsx 直接下载
  */
import React, { useCallback, useState } from 'react';
import type { FilteredData, InitialCosts as InitialCostsType, OperatingCosts as OperatingCostsType, BasicInfo as BasicInfoType } from '../utils/dataModel';
import { calculateDecorationCost, calculateOperatingCosts, calculateResults } from '../utils/dataModel';

interface BatchCalculatorProps {
  filteredData: FilteredData;
}

type AnyObject = Record<string, any>;

const REQUIRED_HEADERS = [
  '省','市','区县','城市梯队','商圈类型','面积/m²','租金模式','固定租金(元/月 )','租金抽成(%)','门店预估业绩(元/月)','门店编号','回本周期','36个月回本租金','36个月回本所需营业额'
];

const normalizeHeader = (h: string): string => h.replace(/\s+/g, '').replace(/ /g, '').trim();

const safeParseNumber = (v: any): number => {
  if (v === null || v === undefined) return 0;
  if (typeof v === 'number') return isFinite(v) ? v : 0;
  const s = String(v).replace(/[,\s]/g, '');
  const n = parseFloat(s);
  return isFinite(n) ? n : 0;
};

const BatchCalculator: React.FC<BatchCalculatorProps> = ({ filteredData }) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 下载批量模板
  const handleDownloadTemplate = useCallback(() => {
    const url = `${process.env.PUBLIC_URL || ''}/【模板】批量计算回本-0910.xlsx`;
    const a = document.createElement('a');
    a.href = url;
    a.download = '【模板】批量计算回本-0910.xlsx';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }, []);

  const handleFileChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(null);
    setIsProcessing(true);
    try {
      // 动态加载 xlsx，避免首屏体积增加
      const xlsx = await import('xlsx');
      const arrayBuffer = await file.arrayBuffer();
      const wb = xlsx.read(arrayBuffer);
      const firstSheetName = wb.SheetNames[0];
      const ws = wb.Sheets[firstSheetName];

      // 获取表头顺序（AOA）
      const aoa: any[][] = xlsx.utils.sheet_to_json(ws, { header: 1, defval: '' });
      if (!aoa || aoa.length === 0) {
        throw new Error('上传的Excel为空');
      }
      const originalHeaders: string[] = (aoa[0] as any[]).map(h => String(h ?? ''));

      // 归一化对照：原始表头 -> 归一化表头
      const normalizedHeaderMap: Record<string, string> = {};
      originalHeaders.forEach((h) => {
        normalizedHeaderMap[normalizeHeader(h)] = h;
      });

      // 校验必需字段是否存在（支持归一化匹配）
      const missing: string[] = [];
      for (const req of REQUIRED_HEADERS) {
        if (!(normalizeHeader(req) in normalizedHeaderMap)) {
          // 末尾三个结果列允许缺失，我们将自动追加
          if (!['回本周期','36个月回本租金','36个月回本所需营业额'].includes(req)) {
            missing.push(req);
          }
        }
      }
      if (missing.length) {
        throw new Error(`Excel缺少必需字段：${missing.join('、')}`);
      }

      // 将数据读为对象数组
      const rows: AnyObject[] = xlsx.utils.sheet_to_json(ws, { defval: '' });

      // 规则与参数准备
      const init = filteredData.initialCostsData;

      const processed: AnyObject[] = rows.map((row) => {
        const getByHeader = (label: string) => row[normalizedHeaderMap[normalizeHeader(label)] ?? label];

        const province = String(getByHeader('省') || '');
        const city = String(getByHeader('市') || '');
        const district = String(getByHeader('区县') || '');
        const cityTierRaw = String(getByHeader('城市梯队') || '');
        const cityTierByMap = filteredData.districtTiers?.[`${province}-${city}-${district}`] || filteredData.cityTiers?.[city] || '';
        const cityTier = cityTierRaw || cityTierByMap || '';

        const businessArea = String(getByHeader('商圈类型') || '');
        const area = safeParseNumber(getByHeader('面积/m²'));
        const fixedRent = safeParseNumber(getByHeader('固定租金(元/月 )'));
        const rentPercentage = safeParseNumber(getByHeader('租金抽成(%)'));
        const estimatedRevenue = safeParseNumber(getByHeader('门店预估业绩(元/月)'));

        const basicInfo: BasicInfoType = {
          province,
          city,
          district,
          cityTier,
          businessArea,
          area,
          estimatedRevenue,
          fixedRent,
          rentPercentage
        };

        const rent = fixedRent + (estimatedRevenue * (rentPercentage || 0) / 100);

        const calculatedCosts = calculateOperatingCosts(estimatedRevenue, cityTier, filteredData.operatingCostRatios, filteredData.operatingCostLimits, filteredData.laborCostRules);

        const operatingCosts: OperatingCostsType = {
          waterElectricity: calculatedCosts.waterElectricity,
          otherStoreCosts: calculatedCosts.otherStoreCosts,
          brandManagementFee: calculatedCosts.brandManagementFee,
          laborCosts: calculatedCosts.laborCosts,
          rent,
          total: calculatedCosts.waterElectricity + calculatedCosts.otherStoreCosts + calculatedCosts.brandManagementFee + calculatedCosts.laborCosts + rent
        };

        // 计算建店成本（固定项 + 装修成本）
        let initialCosts: InitialCostsType = {
          franchiseFee: 0,
          brandUsageFee: 0,
          designFee: 0,
          storeServiceFee: 0,
          equipmentCost: 0,
          decorationCost: 0,
          total: 0
        };

        if (init) {
          const decoration = calculateDecorationCost(
            area,
            init.decorationCostRules || [],
            init.decorationCostMaxLimit,
            init.decorationCostMinLimit
          );
          initialCosts = {
            franchiseFee: init.franchiseFee || 0,
            brandUsageFee: init.brandUsageFee || 0,
            designFee: init.designFee || 0,
            storeServiceFee: init.storeServiceFee || 0,
            equipmentCost: init.equipmentCost || 0,
            decorationCost: decoration,
            total: (init.franchiseFee || 0)
              + (init.brandUsageFee || 0)
              + (init.designFee || 0)
              + (init.storeServiceFee || 0)
              + (init.equipmentCost || 0)
              + decoration
          };
        }

        const results = calculateResults(basicInfo, operatingCosts, initialCosts, filteredData);

        // 写回结果字段（保持原表头名称）
        const out: AnyObject = { ...row };
        out[normalizedHeaderMap[normalizeHeader('回本周期')] || '回本周期'] = Number(results.paybackPeriod.toFixed(1));
        out[normalizedHeaderMap[normalizeHeader('36个月回本租金')] || '36个月回本租金'] = Math.round(results.rent36Month);
        out[normalizedHeaderMap[normalizeHeader('36个月回本所需营业额')] || '36个月回本所需营业额'] = Math.round(results.minRevenueFor36MonthPayback);
        return out;
      });

      // 生成导出表头：以原表头为准，若结果列不存在则追加
      const headerSet = new Set(originalHeaders);
      const exportHeaders = [...originalHeaders];
      const ensureHeader = (h: string) => { if (!headerSet.has(h)) { headerSet.add(h); exportHeaders.push(h); } };
      ensureHeader('回本周期');
      ensureHeader('36个月回本租金');
      ensureHeader('36个月回本所需营业额');

      // 组装为 AOA
      const aoaOut: any[][] = [exportHeaders];
      for (const r of processed) {
        aoaOut.push(exportHeaders.map(h => r[h] ?? ''));
      }

      const outWs = xlsx.utils.aoa_to_sheet(aoaOut);
      const outWb = xlsx.utils.book_new();
      xlsx.utils.book_append_sheet(outWb, outWs, firstSheetName || 'Sheet1');

      const baseName = file.name.replace(/\.xlsx$/i, '');
      const outName = `${baseName}-计算结果.xlsx`;
      xlsx.writeFile(outWb, outName);

      setIsProcessing(false);
    } catch (err: any) {
      console.error('批量计算失败:', err);
      setError(err?.message || '批量计算失败，请检查文件内容');
      setIsProcessing(false);
    } finally {
      // 清空 input，允许重复选择同一文件触发
      e.target.value = '';
    }
  }, [filteredData]);

  const disabled = !filteredData || !filteredData.operatingCostRatios || !filteredData.initialCostsData;

  return (
    <div className="mb-2">
      <details className="text-right">
        <summary className="inline-block text-xs text-gray-500 cursor-pointer select-none hover:text-gray-700">
          更多功能
        </summary>
        <div className="mt-2 flex items-center justify-end gap-2">
          {/* 隐藏原生文件输入，使用自定义按钮与提示文字 */}
          <input
            id="batch-file-input"
            type="file"
            accept=".xlsx"
            onChange={handleFileChange}
            disabled={disabled || isProcessing}
            className="hidden"
          />
          <label
            htmlFor="batch-file-input"
            className={`px-3 py-1 rounded text-xs font-medium cursor-pointer select-none shadow-sm transition-colors 
              ${disabled || isProcessing 
                ? 'bg-blue-400 text-white opacity-60 cursor-not-allowed' 
                : 'bg-blue-600 text-white hover:bg-blue-700'}`}
            title={disabled || isProcessing ? '规则数据未就绪或正在处理中' : '选择Excel文件进行批量计算'}
          >
            点击批量计算回本
          </label>
          <button
            type="button"
            onClick={handleDownloadTemplate}
            className="px-3 py-1 rounded text-xs font-medium border border-blue-600 text-blue-600 bg-white hover:bg-blue-50 shadow-sm transition-colors"
            title="下载批量计算模板"
          >
            下载批量模板
          </button>
          <span className="text-xs text-gray-500"></span>
          {isProcessing && (
            <span className="text-xs text-gray-500">处理中...</span>
          )}
        </div>
      </details>
      {disabled && (
        <div className="text-xs text-yellow-700 mt-1">规则数据尚未加载完成，请稍后重试。</div>
      )}
      {error && (
        <div className="text-xs text-red-700 mt-1">{error}</div>
      )}
    </div>
  );
};

export default BatchCalculator;

